// @ts-check
import { LayoutGrid, List, Plus, RefreshCw, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import atelierAdminService, { PROSPECT_LABELS, PROSPECT_STATUSES } from '../services/atelierAdminService';

const STATUS_STYLES = {
  new: 'bg-muted text-foreground/80',
  contacted: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  replied: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300',
  meeting: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
  negotiating: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
  converted: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  declined: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
};

export default function AtelierCrmPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [view, setView] = useState('kanban');
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        atelierAdminService.listProspects({ search: search || undefined }),
        atelierAdminService.crmStats().catch(() => null),
      ]);
      setProspects(Array.isArray(list) ? list : []);
      setStats(s);
    } catch (e) {
      toast.error(e.message || 'Failed to load CRM');
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Outreach CRM</h1>
          <p className="text-sm text-muted-foreground">Leads → outreach → conversion → orders.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            <button
              onClick={() => setView('kanban')}
              className={`p-2 ${view === 'kanban' ? 'bg-primary text-primary-foreground' : 'bg-card'}`}
              title="Board"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setView('list')}
              className={`p-2 ${view === 'list' ? 'bg-primary text-primary-foreground' : 'bg-card'}`}
              title="List"
            >
              <List size={16} />
            </button>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm flex items-center gap-1"
          >
            <Plus size={15} /> Add
          </button>
          <CsvImport onDone={load} />
          <button onClick={load} className="p-2 rounded-lg hover:bg-accent">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {stats && <StatsBar stats={stats} />}

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search company or email…"
        className="border rounded-lg px-3 py-2 text-sm w-full sm:w-72 mb-4"
      />

      {view === 'kanban' ? (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {PROSPECT_STATUSES.map((st) => {
            const items = prospects.filter((p) => p.status === st);
            return (
              <div key={st} className="min-w-[220px] w-56 flex-shrink-0">
                <div className="text-xs font-medium uppercase text-muted-foreground mb-2 flex justify-between">
                  <span>{PROSPECT_LABELS[st]}</span>
                  <span className="text-muted-foreground/50">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => navigate(`/atelier/crm/${p.id}`)}
                      className="w-full text-left bg-card border rounded-lg p-3 hover:border-black/40"
                    >
                      <div className="font-medium text-sm">{p.company_name}</div>
                      <div className="text-xs text-muted-foreground">{p.contact_name || '—'}</div>
                      <div className="text-xs text-muted-foreground/70 truncate">{p.email}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last contacted</th>
              </tr>
            </thead>
            <tbody>
              {prospects.map((p) => (
                <tr
                  key={p.id}
                  className="border-t hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate(`/atelier/crm/${p.id}`)}
                >
                  <td className="px-4 py-3 font-medium">{p.company_name}</td>
                  <td className="px-4 py-3">{p.contact_name || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${STATUS_STYLES[p.status]}`}>
                      {PROSPECT_LABELS[p.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground/70">
                    {p.last_contacted_at ? new Date(p.last_contacted_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
              {!loading && prospects.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground/70">
                    No prospects
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddProspect onClose={() => setShowAdd(false)} onDone={load} />}
    </div>
  );
}

function StatsBar({ stats }) {
  const f = stats.funnel || {};
  const cards = [
    ['Total leads', stats.total],
    ['Contacted', (f.contacted || 0) + (f.replied || 0) + (f.meeting || 0) + (f.negotiating || 0) + (f.converted || 0)],
    ['Converted', f.converted || 0],
  ];
  return (
    <div className="mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-w-lg mb-3">
        {cards.map(([label, val]) => (
          <div key={label} className="bg-card border rounded-lg p-3">
            <div className="text-2xl font-semibold">{val}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
      {(stats.templates || []).length > 0 && (
        <div className="bg-card border rounded-lg p-3 max-w-2xl">
          <div className="text-xs font-medium text-muted-foreground mb-2">Which email converts</div>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground/70 text-xs">
              <tr>
                <th>Template</th>
                <th>Sent</th>
                <th>Opened</th>
                <th>Converted</th>
                <th>Conv. rate</th>
              </tr>
            </thead>
            <tbody>
              {stats.templates.map((t, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1">{t.template_name}</td>
                  <td>{t.sent}</td>
                  <td>{t.opened}</td>
                  <td>{t.converted}</td>
                  <td>{t.sent ? Math.round((t.converted / t.sent) * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AddProspect({ onClose, onDone }) {
  const toast = useToast();
  const [isDirty, setIsDirty] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    country: '',
    website: '',
    notes: '',
  });
  const set = useCallback((k, v) => {
    setIsDirty(true);
    setForm((f) => ({ ...f, [k]: v }));
  }, []);
  const save = async () => {
    if (!form.company_name || !form.email) {
      toast.error('Company and email required');
      return;
    }
    try {
      await atelierAdminService.createProspect(form);
      toast.success('Prospect added');
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };
  return (
    <Modal
      maxWidth="md"
      fullHeight={false}
      closeOnBackdrop={false}
      className="rounded-lg p-5"
      hasUnsavedChanges={isDirty}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-semibold">Add prospect</h2>
        <button onClick={handleCloseClick}>
          <X size={20} />
        </button>
      </div>
      <div className="space-y-3">
        {[
          ['company_name', 'Company *'],
          ['contact_name', 'Contact name'],
          ['email', 'Email *'],
          ['country', 'Country (ISO 2)'],
          ['website', 'Website'],
        ].map(([k, l]) => (
          <input
            key={k}
            value={form[k]}
            onChange={(e) => set(k, e.target.value)}
            placeholder={l}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        ))}
        <textarea
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Notes"
          rows={2}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
        <button onClick={save} className="w-full bg-primary text-primary-foreground py-2 rounded-lg text-sm">
          Add
        </button>
      </div>
    </Modal>
  );
}

function CsvImport({ onDone }) {
  const toast = useToast();
  const ref = useRef(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const rows = text.split(/\r?\n/).filter((r) => r.trim());
      const header = rows
        .shift()
        .split(',')
        .map((h) => h.trim().toLowerCase());
      const idx = (name) => header.indexOf(name);
      const prospects = rows
        .map((r) => {
          const cols = r.split(',');
          const g = (n) => (idx(n) >= 0 ? (cols[idx(n)] || '').trim() : '');
          return {
            company_name: g('company_name') || g('company'),
            contact_name: g('contact_name') || g('contact'),
            email: g('email'),
            country: g('country'),
            website: g('website'),
          };
        })
        .filter((p) => p.company_name && p.email);
      if (prospects.length === 0) {
        toast.error('No valid rows (need company_name + email columns)');
        return;
      }
      const res = await atelierAdminService.importProspects(prospects);
      toast.success(`Imported ${res.added}, skipped ${res.skipped}`);
      onDone();
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      e.target.value = '';
    }
  };

  return (
    <>
      <button
        onClick={() => ref.current?.click()}
        className="px-3 py-2 rounded-lg bg-muted text-sm flex items-center gap-1"
      >
        <Upload size={15} /> CSV
      </button>
      <input ref={ref} type="file" accept=".csv" className="hidden" onChange={handle} />
    </>
  );
}
